// QC Inspector Qualifications — list page
// GAP-012 closure (AISC 207-25 §2.3.1 + BU2.5 + ANSI/AISC 360 §N4)

import React, { useEffect, useState, useCallback } from "react";
import PageGuide from "../../components/PageGuide";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Container, Stack, Typography, Breadcrumbs, Button, Paper, Box, Alert,
  TableHead, TableRow, TableCell, TableBody, TableContainer,
  Chip, FormControl, InputLabel, Select, MenuItem, CircularProgress,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";

import { qciApi, QCI_METHOD_LABELS, QCI_STATUS_LABELS, QCI_STATUS_COLORS, isExpiringSoon } from "../../utils/qcInspectorQualificationsApi";

import SmartTable from "../../components/SmartTable/SmartTable";
export default function QcInspectorQualifications() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await qciApi.list(params);
      setRows(res?.data?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed to load");
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <Container maxWidth="xl">
      <Helmet><title>QC Inspector Qualifications | SFM</title></Helmet>
      <Breadcrumbs sx={{ mb: 1 }}>
        <RouterLink to="/dashboard/app" style={{ color: "inherit" }}>Home</RouterLink>
        <Typography color="text.primary">QC Inspector Qualifications</Typography>
      </Breadcrumbs>

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4">QC Inspector Qualifications</Typography>
          <Typography variant="body2" color="text.secondary">
            AISC 207-25 §2.3.1 + BU2.5 — qualified per ANSI/AISC 360 §N4 with documented training + experience.
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => navigate("/qc-inspector-qualifications/new")}>
          + Add Inspector
        </Button>
      </Stack>
      <PageGuide
        why="Show your QC inspectors are qualified to the codes they inspect to — required for AISC certification."
        who="The QM, proving QC inspector qualifications for certification."
        what="A record of each inspector's certifications (e.g., AWS CWI) and qualifications."
        how="Add an inspector and record their certifications and expiry dates."
        when="Before an inspector signs off work, and as certifications come up for renewal."
        standard="AISC 360 §N4 · AISC 207-25 §2.3.1"
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Tenant-flexibility canon:</strong> SFM does not seed a default qualification taxonomy. Each tenant defines acceptable qualification methods (ASNT, AWS-CWI, internal training, etc.) per their QMS.
      </Alert>

      <Paper sx={{ p: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="suspended">Suspended</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
            <MenuItem value="revoked">Revoked</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <SmartTable tableId="qc-inspector-qualifications" lockedColumns={["Inspector", "Status", "Cert Expires"]} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Inspector</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Internal/Contracted</TableCell>
                <TableCell>Cert Expires</TableCell>
                <TableCell>Experience (yrs)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                  No QC Inspector qualifications recorded. Add one to begin tracking per BU2.5.
                </TableCell></TableRow>
              ) : rows.map((r) => {
                const expiring = isExpiringSoon(r.certified_to_date);
                return (
                  <TableRow key={r.id} hover sx={{ cursor: "pointer" }} onClick={() => navigate(`/qc-inspector-qualifications/${r.id}`)}>
                    <TableCell><strong>{r.inspector_name}</strong></TableCell>
                    <TableCell>{QCI_METHOD_LABELS[r.qualification_method] || r.qualification_method}</TableCell>
                    <TableCell>{r.qualification_class}</TableCell>
                    <TableCell>
                      <Chip label={QCI_STATUS_LABELS[r.status]} color={QCI_STATUS_COLORS[r.status]} size="small" />
                    </TableCell>
                    <TableCell>{r.is_internal ? "Internal" : `Contracted (${r.contractor_company_name || "—"})`}</TableCell>
                    <TableCell sx={{ color: expiring ? "warning.dark" : undefined, fontWeight: expiring ? 600 : 400 }}>
                      {r.certified_to_date || "—"} {expiring && "⚠"}
                    </TableCell>
                    <TableCell>{r.documented_experience_years || "—"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </SmartTable>
        </TableContainer>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        {rows.length} inspector{rows.length === 1 ? "" : "s"} on file. Auditor walks this register per BU2.5.
      </Typography>
    </Container>
  );
}
